import { type Offer } from "@core/offers/types";
import { formatRelativeTime, money, URLS } from "@core/shared/utils";
import { trans } from "@mongez/localization";
import { Link } from "@remix-run/react";
import { BookOpenCheck, Clock3, TicketPercent } from "lucide-react";
import { useEffect, useState } from "react";

type OfferCardProps = {
  offer: Offer;
};

export function OfferCard({ offer }: OfferCardProps) {
  const {
    name,
    image,
    description,
    price,
    oldPrice,
    salePercentage,
    lessons,
    endsAt,
  } = offer;

  const isFree = price === 0;
  const formattedPrice = isFree ? trans("free") : money(price);
  const formattedOldPrice = money(oldPrice);
  const savingsAmount = oldPrice - price;

  // Countdown timer state
  const [timeRemaining, setTimeRemaining] = useState<string | null>(null);
  const [isExpired, setIsExpired] = useState(false);

  // Update countdown timer
  useEffect(() => {
    if (!endsAt) return;

    // Calculate time remaining if offer has end date
    const calculateTimeRemaining = () => {
      const now = new Date();
      const endDate = new Date(endsAt.timestamp);
      const timeDiff = endDate.getTime() - now.getTime();

      if (timeDiff <= 0) {
        setIsExpired(true);
        return null;
      }

      return formatRelativeTime(endsAt);
    };

    const updateTimer = () => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);
    };

    // Initial calculation
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [endsAt]);

  // Fallback image component
  const FallbackImage = () => (
    <div className="relative flex aspect-[16/10] w-full items-center justify-center overflow-hidden bg-gradient-to-br from-primary via-violet-600 to-fuchsia-500 dark:from-primary/80 dark:via-violet-900 dark:to-slate-900">
      {/* Clean geometric pattern */}
      <div className="absolute inset-0 opacity-5 dark:opacity-10">
        <div className="absolute left-4 top-4 h-16 w-16 rounded-full border-2 border-white"></div>
        <div className="absolute right-8 top-8 h-8 w-8 rounded-full border border-white"></div>
        <div className="absolute bottom-8 left-8 h-12 w-12 rounded-lg border-2 border-white"></div>
        <div className="absolute bottom-4 right-4 h-6 w-6 rounded-full border border-white"></div>
      </div>

      {/* Subtle radial gradient overlay for depth */}
      <div className="bg-gradient-radial absolute inset-0 from-transparent via-transparent to-black/20 dark:to-black/40"></div>

      {/* Main content */}
      <div className="z-10 px-4 text-center text-white">
        <span className="mx-auto mb-3 grid size-14 place-items-center rounded-2xl bg-white/15 backdrop-blur">
          <TicketPercent className="size-7" />
        </span>
        <h3 className="mb-1 line-clamp-2 text-lg font-bold">{name}</h3>
        {lessons !== undefined && lessons.length > 0 && (
          <div className="text-sm opacity-90">
            {trans("grades.totalLessons", {
              count: lessons.length,
            })}
          </div>
        )}
      </div>

      {/* Sale badge overlay */}
      {salePercentage > 0 && (
        <div className="absolute left-3 top-3 rounded-full bg-red-500 px-2 py-1 text-xs font-bold text-white shadow-lg dark:bg-red-600">
          {salePercentage}% {trans("offers.off")}
        </div>
      )}

      {/* Countdown timer badge */}
      {timeRemaining && !isExpired && (
        <div className="absolute right-3 top-3 animate-pulse rounded-full bg-yellow-500 px-2 py-1 text-xs font-bold text-white shadow-lg dark:bg-yellow-600">
          {timeRemaining}
        </div>
      )}

      {/* Expired badge */}
      {isExpired && (
        <div className="absolute right-3 top-3 rounded-full bg-gray-500 px-2 py-1 text-xs font-bold text-white shadow-lg dark:bg-gray-600">
          {trans("offers.offerExpired")}
        </div>
      )}
    </div>
  );

  return (
    <Link
      to={URLS.offers.view(offer)}
      className="group flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-slate-200/80 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-xl dark:border-slate-800 dark:bg-slate-900"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-slate-100 dark:bg-slate-800">
        {image ? (
          <>
            <img
              src={image.url}
              alt={name}
              className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent" />
            {salePercentage > 0 && (
              <div className="absolute start-3 top-3 rounded-full bg-rose-500 px-3 py-1.5 text-xs font-black text-white shadow-lg">
                {salePercentage}% {trans("offers.off")}
              </div>
            )}
            {/* Countdown timer badge */}
            {timeRemaining && !isExpired && (
              <div className="absolute end-3 top-3 inline-flex items-center gap-1 rounded-full bg-amber-400 px-3 py-1.5 text-xs font-black text-slate-950 shadow-lg">
                <Clock3 className="size-3" />
                {timeRemaining}
              </div>
            )}
            {/* Expired badge */}
            {isExpired && (
              <div className="absolute end-3 top-3 rounded-full bg-slate-700 px-3 py-1.5 text-xs font-bold text-white shadow-lg">
                {trans("offers.offerExpired")}
              </div>
            )}
          </>
        ) : (
          <FallbackImage />
        )}
      </div>

      <div className="flex flex-1 flex-col p-5">
        <h3 className="line-clamp-2 min-h-14 text-lg font-black leading-7 text-slate-950 transition group-hover:text-primary dark:text-white">
          {name}
        </h3>

        {description && (
          <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
            {description}
          </p>
        )}

        <div className="mt-4 flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2.5 text-sm font-bold text-slate-600 dark:bg-slate-800/80 dark:text-slate-300">
          <BookOpenCheck className="size-4 text-primary" />
          <span>
            {trans("grades.totalLessons", {
              count: lessons?.length || 0,
            })}
          </span>
        </div>

        <div className="mt-auto flex items-end justify-between gap-3 pt-5">
          <div className="flex items-center gap-2">
            <span
              className={`text-xl font-black ${isFree ? "text-emerald-600 dark:text-emerald-400" : "text-slate-950 dark:text-white"}`}
            >
              {formattedPrice}
            </span>
            {oldPrice > price && !isFree && (
              <span className="text-sm text-gray-500 line-through dark:text-gray-400">
                {formattedOldPrice}
              </span>
            )}
          </div>

          {oldPrice > price && !isFree && (
            <div className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-black text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
              {trans("offers.save")} {money(savingsAmount)}
            </div>
          )}
        </div>

        {/* Countdown timer in card body for longer offers */}
        {timeRemaining && !isExpired && endsAt && (
          <div className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-amber-50 px-3 py-2.5 dark:bg-amber-900/20">
            <Clock3 className="size-4 text-amber-600 dark:text-amber-400" />
            <span className="text-sm font-bold text-amber-800 dark:text-amber-300">
              {timeRemaining.includes("d")
                ? `${trans("offers.validUntil")}: ${timeRemaining}`
                : `${trans("offers.expiresSoon")}: ${timeRemaining}`}
            </span>
          </div>
        )}
      </div>
    </Link>
  );
}
